import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import SalesProgressionTab from './Sales/SalesProgressionTab';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSalesOnboardingMap } from '../services/api/implementation/impl';
import { SalesList } from './SalesList';
import PreleadList from './PreleadList';
import CustomerList from './CustomerList';
import ConsolidatedList from './ConsolidatedList';
import UnderConsiderationList from './UnderConsiderationList';
import SalesDashboard from './Sales/SalesDashboard';
import SalesUnitInventory from './Sales/SalesUnitInventory';
import TargetsAndIncentives from './TargetsAndIncentives';
import PropertyDocuments from './PropertyDocuments';
import WhatsAppInbox from './WhatsAppInbox';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useBuilderWhatsappIntegration } from '../hooks/useBuilderWhatsappIntegration';
import { useInboxUnread } from '../hooks/useInboxUnread';
import { useInboxStream } from '../hooks/useInboxStream';
// import Modal from '../../shared/components/ui/Modal';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { isDominoEnabled } from '../../shared/utils/authStorage';
import { resolveAiFilterStatus } from '../components/ai/aiListDeepLinks';
import {
  buildSalesMainTabParams,
  getSalesDashboardFiltersFromSearch,
  getSalesMainTabFromSearch,
  getSalesSubViewFromSearch
} from './Sales/salesUrlParams';
import SalesGlobalContactSearch from './Sales/SalesGlobalContactSearch';

const salesHeaderNavClass = (active) =>
  `relative px-3 py-1.5 rounded-[8px] text-sm font-medium border transition-all duration-200 shrink-0 ${
    active
      ? 'bg-[#2A565E] text-white border-[#2A565E]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
  }`;

const SalesMain = () => {
  const { id: projectId } = useParams();
  const { collapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const userVertical = Cookies.get('userVertical');
  const isManagementVertical = userVertical?.toLowerCase() === 'management';
  const showProgressionTab = !isManagementVertical && isDominoEnabled();
  const { allowWhatsAppCampaignsSidebar } = useBuilderWhatsappIntegration();
  const { unread_conversations: inboxUnreadCount = 0 } = useInboxUnread({
    enabled: allowWhatsAppCampaignsSidebar
  });
  const salesUserId = Number(Cookies.get('userId'));
  const isTotalLeadsRoute = location.pathname.includes('/builder/sales/total-leads');
  const status = location.state?.status;
  const projectIDFromState = location.state?.projectID ?? null;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const aiFilter = searchParams.get('aiFilter');
  // Only force a project on Leads for total-leads deep links or explicit project_id (AI).
  // Dashboard projectID must not lock the Leads list — default is All Projects.
  const projectIdFromQuery = searchParams.get('project_id');
  const userId = Cookies.get('userId');
  const urlDashboardFilters = useMemo(
    () => getSalesDashboardFiltersFromSearch(searchParams),
    [searchParams]
  );

  const syncSalesMainUrl = useCallback(
    ({ view, tab, projectID, month, clearListProject = false } = {}) => {
      const params = buildSalesMainTabParams(searchParams, {
        view,
        tab,
        projectID:
          view === 'leads'
            ? null
            : projectID ?? urlDashboardFilters.projectID ?? projectIdFromQuery,
        month: view === 'leads' ? null : month ?? urlDashboardFilters.month,
        // Tab click / default leads entry → All Projects. Dashboard redirects keep project_id.
        clearListProject: view === 'leads' ? clearListProject : false
      });
      const qs = params.toString();
      navigate(
        {
          pathname: location.pathname.includes('/total-leads')
            ? location.pathname
            : '/builder/sales/',
          search: qs ? `?${qs}` : ''
        },
        { replace: true }
      );
    },
    [
      navigate,
      location.pathname,
      searchParams,
      urlDashboardFilters.projectID,
      urlDashboardFilters.month,
      projectIdFromQuery
    ]
  );

  // Track component mount

  // Track page view
  useEffect(() => {
    mixpanel.track('Sales Main Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    mixpanel.track('Sales Main Viewed', {
      user_id: userId,
      project_id: projectId,
      initial_status: status,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Helper function to get saved tab from localStorage
  const getSavedPrimaryTab = () => {
    try {
      return localStorage.getItem('salesMainPrimaryTab') || 'Details';
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return 'Details';
    }
  };

  // Helper function to save tab to localStorage
  const savePrimaryTab = (tab) => {
    try {
      localStorage.setItem('salesMainPrimaryTab', tab);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Helper function to get saved secondary tab from localStorage
  const getSavedSecondaryTab = () => {
    try {
      const t = localStorage.getItem('salesMainSecondaryTab') || 'Leads';
      if (t === 'Prequalified List') {
        localStorage.setItem('salesMainSecondaryTab', 'Leads');
        return 'Leads';
      }
      return t;
    } catch (error) {
      console.error('Error reading secondary tab from localStorage:', error);
      return 'Leads';
    }
  };

  // Helper function to save secondary tab to localStorage
  const saveSecondaryTab = (tab) => {
    try {
      localStorage.setItem('salesMainSecondaryTab', tab);
    } catch (error) {
      console.error('Error saving secondary tab to localStorage:', error);
    }
  };

  /* 
        Data fetching
      */

  /* 
        Data mutation
      */

  /*
        Hooks 
      */

  /* 
        Local state 
      */
  const [primaryTab, setPrimaryTab] = useState(getSavedPrimaryTab());
  const [secondaryTab, setSecondaryTab] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('/builder/sales/total-leads')) {
      return 'Leads';
    }
    const fromUrl = getSalesMainTabFromSearch(
      new URLSearchParams(window.location.search),
      { localStorageFallback: getSavedSecondaryTab() }
    );
    if (status) return 'Leads';
    return fromUrl;
  });
  const [statusFilter, setStatusFilter] = useState(status || null);

  /** On total-leads URLs, drive lead temperature filter from the path or ?aiFilter= */
  const salesListStatusFilter = useMemo(() => {
    const p = location.pathname.replace(/\/$/, '');
    if (p.endsWith('/total-leads/hot')) return ['Hot'];
    if (p.endsWith('/total-leads/warm')) return ['Warm'];
    if (p.endsWith('/total-leads/cold')) return ['Cold'];
    if (p.endsWith('/total-leads/nurture')) return ['Nurture'];
    if (p.endsWith('/total-leads/future-prospect')) return ['Future Prospect'];
    const fromQuery = resolveAiFilterStatus(aiFilter);
    if (fromQuery) return fromQuery;
    if (p.includes('/builder/sales/total-leads')) return [];
    return statusFilter;
  }, [location.pathname, statusFilter, aiFilter]);

  // Force project on Leads only for: total-leads routes, AI project_id, or dashboard metric redirect (state/query).
  const salesListForcedProjectId = isTotalLeadsRoute
    ? projectIdFromQuery || projectIDFromState || null
    : projectIdFromQuery || projectIDFromState || null;

  const [OpenAccommodation, setOpenAccommodation] = useState(false);
  const [isOpenAddDetailsModal, setIsOpenAddDetailsModal] = useState(false);
  const [isOpenBlockDetailingModal, setIsOpenBlockDetailingModal] = useState(false);
  const [isOpenUnitDetailingModal, setIsOpenUnitDetailingModal] = useState(false);
  const [isOpenImportAccommodation, setisOpenImportAccommodation] = useState(false);
  const [isOpenEditAccommodation, setIsOpenEditAccommodation] = useState(false);
  /** 'dashboard' | 'leads' | 'progression' | 'inbox' — which view sits under the Sales header */
  const [salesSubView, setSalesSubView] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('/builder/sales/total-leads')) {
      return 'leads';
    }
    const fromUrl = getSalesSubViewFromSearch(new URLSearchParams(window.location.search));
    if (fromUrl) return fromUrl;
    const tab = new URLSearchParams(window.location.search).get('tab');
    if (tab && tab !== 'Leads') return 'leads';
    return 'dashboard';
  });

  // Single SSE owner for Sales — SalesList registers its lead-row updater when mounted.
  const salesListConversationHandlerRef = useRef(null);
  const registerSalesListConversationHandler = useCallback((handler) => {
    salesListConversationHandlerRef.current = handler;
  }, []);
  const handleSalesConversationUpdated = useCallback((data) => {
    salesListConversationHandlerRef.current?.(data);
  }, []);

  useInboxStream({
    enabled: allowWhatsAppCampaignsSidebar,
    onConversationUpdated: handleSalesConversationUpdated
  });
  const [dashboardFilters, setDashboardFilters] = useState({
    projectID: projectIDFromState || urlDashboardFilters.projectID,
    month: location.state?.month || urlDashboardFilters.month || null
  });
  const hasDashboardProjectSelected =
    dashboardFilters.projectID !== null &&
    dashboardFilters.projectID !== undefined &&
    String(dashboardFilters.projectID) !== '';
  const resolvedDashboardProjectId = hasDashboardProjectSelected
    ? Number(dashboardFilters.projectID)
    : null;

  useEffect(() => {
    if (!showProgressionTab && salesSubView === 'progression') {
      setSalesSubView('dashboard');
    }
  }, [showProgressionTab, salesSubView]);

  useEffect(() => {
    if (!allowWhatsAppCampaignsSidebar && salesSubView === 'inbox') {
      setSalesSubView('dashboard');
    }
  }, [allowWhatsAppCampaignsSidebar, salesSubView]);

  // Deep-link ?aiFilter= / total-leads routes — open leads list without changing header chrome.
  useEffect(() => {
    if (!location.pathname.includes('/builder/sales/total-leads') && !aiFilter) return;
    setSalesSubView('leads');
    setSecondaryTab('Leads');
  }, [location.pathname, aiFilter]);

  const { data: onboardingResponse } = useQuery({
    queryKey: ['salesOnboardingMap', resolvedDashboardProjectId, salesUserId],
    queryFn: () => getSalesOnboardingMap(resolvedDashboardProjectId, salesUserId),
    enabled: showProgressionTab && !!resolvedDashboardProjectId && !!salesUserId
  });

  const progressionPendingCount = useMemo(() => {
    const progress = onboardingResponse?.data?.progress;
    if (progress?.total != null && progress?.completed != null) {
      return Math.max(0, progress.total - progress.completed);
    }

    return (onboardingResponse?.data?.steps || []).filter(
      (step) => step.status !== 'completed'
    ).length;
  }, [onboardingResponse]);

  const handleDashboardFiltersChange = useCallback(
    ({ projectID, month }) => {
      setDashboardFilters({ projectID, month });
      syncSalesMainUrl({
        view: salesSubView,
        tab: secondaryTab,
        projectID,
        month
      });
    },
    [syncSalesMainUrl, salesSubView, secondaryTab]
  );

  const getSalesTabLabel = useCallback(
    (tab) => {
      if (tab === 'Progression' && hasDashboardProjectSelected) {
        return `Progression (${String(progressionPendingCount).padStart(2, '0')})`;
      }
      return tab;
    },
    [hasDashboardProjectSelected, progressionPendingCount]
  );

  useEffect(() => {
    if (location.pathname.includes('/builder/sales/total-leads')) {
      setSalesSubView('leads');
      setSecondaryTab('Leads');
      saveSecondaryTab('Leads');
    }
  }, [location.pathname]);

  useEffect(() => {
    const tabFromUrl = getSalesMainTabFromSearch(searchParams);
    const viewFromUrl = getSalesSubViewFromSearch(searchParams);
    const secondaryTabFromState = location.state?.secondaryTab;

    if (secondaryTabFromState === 'Inbox') {
      if (allowWhatsAppCampaignsSidebar) {
        setSalesSubView('inbox');
      }
      return;
    }
    if (secondaryTabFromState) {
      setSecondaryTab(secondaryTabFromState);
      saveSecondaryTab(secondaryTabFromState);
      setSalesSubView('leads');
      if (
        searchParams.get('tab') !== secondaryTabFromState ||
        searchParams.get('view') !== 'leads'
      ) {
        syncSalesMainUrl({ view: 'leads', tab: secondaryTabFromState });
      }
      return;
    }

    if (viewFromUrl) {
      setSalesSubView(viewFromUrl);
    }
    if (searchParams.get('tab') && tabFromUrl) {
      setSecondaryTab(tabFromUrl);
      saveSecondaryTab(tabFromUrl);
      if (!viewFromUrl || viewFromUrl === 'leads') {
        setSalesSubView('leads');
      }
    }

    const statusFromState = location.state?.status;
    if (statusFromState) {
      setSecondaryTab('Leads');
      setStatusFilter(statusFromState);
      setSalesSubView('leads');
      saveSecondaryTab('Leads');
    }
  }, [location.state, location.search, allowWhatsAppCampaignsSidebar, searchParams, syncSalesMainUrl]);

  /* 
        Meta data
      */
  const tabbedComponentMetaData = {
    tabs: ['Details', 'Documents', 'Schedule']
  };

  // const secondaryTabbedComponentMetaData = {
  //   tabs: ['Leads','Under Consideration', 'Closed Customers', 'Invoices', 'Payment', 'Consolidated View of Sales']
  // };

  const salesSecondaryTabs = [
    'Prelead List',
    'Leads',
    'Unit Inventory',
    'Targets & Incentives',
    'Property Templates',
    ...(showProgressionTab ? ['Progression'] : []),
    ...(isManagementVertical ? ['Revenue'] : [])
  ];
  const unifiedSalesTabs = [
    'Dashboard',
    ...salesSecondaryTabs,
    ...(allowWhatsAppCampaignsSidebar ? ['Inbox'] : [])
  ];

  /* 
        Event handlers
      */
  const handleChangePrimaryTab = useCallback(
    (tabName) => {
      setPrimaryTab(tabName);
      savePrimaryTab(tabName); // Save to localStorage when tab changes

      // Track primary tab change
      mixpanel.track('Sales Main Primary Tab Changed', {
        user_id: userId,
        tab: tabName,
        project_id: projectId,
        timestamp: new Date().toISOString()
      });
    },
    [userId, projectId]
  );

  const handleChangeSecondaryTab = useCallback(
    (tabName) => {
      setSecondaryTab(tabName);
      saveSecondaryTab(tabName);
      if (tabName === 'Leads') {
        setStatusFilter(null);
      }
      // Header tab → All Projects. Dashboard metric redirects set project_id themselves.
      syncSalesMainUrl({
        view: 'leads',
        tab: tabName,
        clearListProject: tabName === 'Leads'
      });

      // Track secondary tab change
      mixpanel.track('Sales Main Secondary Tab Changed', {
        user_id: userId,
        tab: tabName,
        project_id: projectId,
        timestamp: new Date().toISOString()
      });
    },
    [userId, projectId, syncSalesMainUrl]
  );

  /* 
        Effects
      */
  useEffect(() => {
    if (isOpenAddDetailsModal) {
      // console.log('Preventing scroll');
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpenAddDetailsModal]);

  /* 
        JSX
      */
  return (
    <>
      {/* Main content */}
      <Layout>
        {/* Header */}
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[60px] w-[calc(100%-60px)]' : 'left-[15rem] w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: `` }} backArrow={false}>
            <div className="flex w-full min-w-0 items-center gap-3 py-1">
              <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap hide-scrollbar">
                {unifiedSalesTabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => {
                      if (tab === 'Dashboard') {
                        setSalesSubView('dashboard');
                        syncSalesMainUrl({ view: 'dashboard', tab: secondaryTab });
                        return;
                      }
                      if (tab === 'Inbox') {
                        setSalesSubView('inbox');
                        if (location.pathname !== '/builder/sales/' && location.pathname !== '/builder/sales') {
                          navigate('/builder/sales/', { state: { secondaryTab: 'Inbox' } });
                        }
                        return;
                      }
                      if (tab === 'Progression') {
                        setSalesSubView('progression');
                        syncSalesMainUrl({ view: 'progression', tab: secondaryTab });
                        return;
                      }
                      setSalesSubView('leads');
                      handleChangeSecondaryTab(tab);
                    }}
                    className={salesHeaderNavClass(
                      tab === 'Dashboard'
                        ? salesSubView === 'dashboard'
                        : tab === 'Inbox'
                          ? salesSubView === 'inbox'
                          : tab === 'Progression'
                            ? salesSubView === 'progression'
                            : salesSubView === 'leads' && secondaryTab === tab
                    )}>
                    <span className="inline-flex items-center gap-1.5">
                      {getSalesTabLabel(tab)}
                      {tab === 'Inbox' && inboxUnreadCount > 0 ? (
                        <span
                          className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none ${
                            salesSubView === 'inbox'
                              ? 'bg-white text-[#2A565E]'
                              : 'bg-primary text-white'
                          }`}
                          aria-label={`${inboxUnreadCount} unread conversation${inboxUnreadCount === 1 ? '' : 's'}`}>
                          {inboxUnreadCount > 99 ? '99+' : inboxUnreadCount}
                        </span>
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
              <SalesGlobalContactSearch />
            </div>
          </Header>
        </div>

        {/* Primary Tabbed content */}
        {/* <div className="w-full fixed top-[4rem] z-50 bg-orange-600">
                    <PrimaryTabbedComponent
                        width={10}
                        data={tabbedComponentMetaData}
                        activePrimary={primaryTab}
                        onChangePrimaryTab={(tab) => handleChangePrimaryTab(tab)}
                    />
                </div> */}

        {showProgressionTab && salesSubView === 'progression' && (
          <SalesProgressionTab
            filters={dashboardFilters}
            onFiltersChange={handleDashboardFiltersChange}
          />
        )}

        {salesSubView === 'dashboard' && (
          <SalesDashboard
            skipLayout
            skipHeader
            projectID={dashboardFilters.projectID}
            month={dashboardFilters.month}
            onFiltersChange={handleDashboardFiltersChange}
          />
        )}

        {allowWhatsAppCampaignsSidebar && salesSubView === 'inbox' && (
          <div
            className={`bg-[#F8F9FA] mt-[4.4rem] ${collapsed ? 'ml-[1vw]' : ''}`}
            style={{ '--wa-campaign-shell-height': '4.4rem' }}>
            <WhatsAppInbox />
          </div>
        )}

        {salesSubView === 'leads' && (
          <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
            {/* Overview content */}
            {secondaryTab === 'Prelead List' && <PreleadList />}

            {secondaryTab === 'Leads' && (
              <div className="w-full h-full">
                <SalesList
                  statusFilter={salesListStatusFilter}
                  forcedProjectID={salesListForcedProjectId}
                  aiFilter={aiFilter}
                  variant={isTotalLeadsRoute ? 'list' : 'metrics'}
                  ownInboxStream={false}
                  onRegisterConversationHandler={registerSalesListConversationHandler}
                />
              </div>
            )}

            {secondaryTab === 'Unit Inventory' && (
              <div className="w-full h-full">
                <SalesUnitInventory
                  embedded
                  forcedProjectID={projectIDFromState}
                  openBulkUnitUpdate={location.state?.openBulkUnitUpdate}
                />
              </div>
            )}

            {secondaryTab === 'Under Consideration' && (
              <div className="w-full h-full mt-[4.4rem]">
                <UnderConsiderationList forcedProjectID={projectIDFromState} />
              </div>
            )}

            {/* PLC content */}
            {primaryTab === 'Details' && secondaryTab === `Closed Won` && (
              <div className=" w-full h-full ">
                <CustomerList forcedProjectID={projectIDFromState} />
              </div>
            )}

            {primaryTab === 'Details' && secondaryTab === `Revenue` && (
              <div className=" w-full h-full ">
                <ConsolidatedList />
              </div>
            )}

            {secondaryTab === 'Targets & Incentives' && <TargetsAndIncentives embedded={true} />}

            {secondaryTab === 'Property Templates' && (
              <div className="w-full min-h-full bg-gray-100 mt-[4.4rem]">
                <PropertyDocuments from={'sales'} />
              </div>
            )}
          </div>
        )}
      </Layout>
    </>
  );
};

export default SalesMain;
